import { useRef } from "react";
import { isCancel, isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { updateFeaturePermission } from "./updateFeaturePermission";

export function useLabsModeToggle() {
  const { t } = useTranslation("common");
  const { isLabs, setMe, role } = useMeStore();
  const { closeHandler, isLabsTabOpen } = useProjectSidebarStore();
  const labsModeAvailable = useFeatureFlag("labs-mode");
  const abortControllerRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const setEnabled = async (checked: boolean) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setMe({ isLabs: checked });

    if (!checked && isLabsTabOpen()) {
      closeHandler();
    }

    try {
      await updateFeaturePermission(checked ? 1 : 0, abortControllerRef.current.signal);
    } catch (error) {
      if (isCancel(error)) return;
      if (isAxiosError(error) && error.response?.status === 403) {
        queryClient.invalidateQueries({ queryKey: ["organization-feature-flags"] });
        setMe({ isLabs: false });
        toast.error("Labs mode has been disabled for current organization");
        return;
      }
      console.error(error);
      toast.error(checked ? t("labsMode.enableFailed") : t("labsMode.disableFailed"));
      setMe({ isLabs: !checked });
    }
  };

  return { available: labsModeAvailable || isSystemAdmin(role), enabled: !!isLabs, setEnabled };
}
